import React, { useState } from "react";
import { AlertTriangle, Sparkles, ShieldCheck, Clock, LinkIcon, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

const TIER_META = {
  A: { label: "Tier A · Strongest", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  B: { label: "Tier B · Strong", cls: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300" },
  C: { label: "Tier C · Supporting", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  D: { label: "Tier D · Low strength", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

const STATE_META = {
  verified_positive: { label: "Verified positive", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  verified_negative: { label: "Verified concern", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  insufficient: { label: "Insufficient evidence", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  not_applicable: { label: "N/A", cls: "bg-muted text-muted-foreground" },
};

const ConfBadge = ({ level }) => {
  const map = {
    high: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    low: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 ${map[level] || map.medium}`}>
      <ShieldCheck className="h-3 w-3" /> {level || "medium"} confidence
    </span>
  );
};

const CriterionCard = ({ b }) => {
  const state = STATE_META[b.evidence_state] || STATE_META.insufficient;
  const tier = TIER_META[b.source_tier] || TIER_META.D;
  return (
    <div className="rounded-2xl border border-border p-4 space-y-2" data-testid={`crit-${b.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm">{b.label}</div>
          <div className="text-[11px] text-muted-foreground">Weight {b.weight}% · Scope: {b.scope || "product"}</div>
        </div>
        <div className="mono text-sm font-bold whitespace-nowrap">{b.score}<span className="text-muted-foreground">/100</span>
          <span className="ml-2 text-[10px] text-muted-foreground">→ {b.achieved_points ?? b.weighted_points} pts</span>
        </div>
      </div>
      <Progress value={b.score} className="h-1.5" />
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${state.cls}`}>{state.label}</span>
        <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${tier.cls}`} title="Source strength">{tier.label}</span>
        {b.confidence && b.confidence !== "insufficient" && (
          <span className="text-[10px] text-muted-foreground uppercase tracking-widest">· {b.confidence} conf.</span>
        )}
      </div>
      {b.evidence && (
        <p className="text-xs text-muted-foreground leading-relaxed pt-1"><span className="font-semibold text-foreground">Finding: </span>{b.evidence}</p>
      )}
      {b.reason && b.reason !== b.evidence && (
        <p className="text-xs text-muted-foreground leading-relaxed"><span className="font-semibold text-foreground">Reason: </span>{b.reason}</p>
      )}
      {b.source_url && (
        <a href={b.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
          <ExternalLink className="h-3 w-3" /> View source
        </a>
      )}
    </div>
  );
};

const MethodologyPanel = ({ product, methodology }) => {
  const [reason, setReason] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  if (!methodology || methodology.error) {
    return (
      <div className="card-premium p-6 text-center text-muted-foreground text-sm">
        <AlertTriangle className="h-6 w-6 mx-auto opacity-50" />
        <p className="mt-2">AI methodology not yet generated for this product.</p>
        {methodology?.error && <p className="mt-1 text-xs opacity-70">Error: {methodology.error}</p>}
      </div>
    );
  }

  const submitContest = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 15) return toast.error("Please provide at least 15 characters of feedback");
    setSending(true);
    try {
      await api.post(`/products/${product.id}/contest`, { reason, email: email || undefined });
      toast.success("Thanks — we'll review your feedback.");
      setReason(""); setEmail(""); setOpen(false);
    } catch (e) { toast.error("Failed to submit"); } finally { setSending(false); }
  };

  const breakdown = methodology.breakdown || [];

  return (
    <div className="space-y-6" data-testid="methodology-panel">
      {/* Header */}
      <div className="card-premium p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Category · {product.category}</div>
              <div className="text-sm font-semibold">Methodology v{methodology.methodology_version || methodology.category_version || "1.0"} · Engine {methodology.intelligence_engine_version || "v1"}</div>
            </div>
          </div>
          <ConfBadge level={methodology.confidence} />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Generated {methodology.generated_at && new Date(methodology.generated_at).toLocaleString()} · Model: {methodology.model}
          {" · "}
          {methodology.evidence_criteria_count}/{methodology.total_criteria_count} criteria with evidence
        </p>
      </div>

      {/* Weight distribution */}
      {breakdown.length > 0 && (
        <div className="card-premium p-6">
          <h4 className="font-bold text-sm uppercase tracking-widest text-muted-foreground mb-3">Scoring breakdown</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {breakdown.map((b) => <CriterionCard key={b.key} b={b} />)}
          </div>
        </div>
      )}

      {/* Sources */}
      <div className="card-premium p-6">
        <h4 className="font-bold text-sm uppercase tracking-widest text-muted-foreground mb-3">Sources analyzed</h4>
        <ul className="space-y-2.5 text-sm">
          <li className="flex items-start gap-2"><FileText className="h-4 w-4 text-primary mt-0.5" /> Product name & description you submitted</li>
          {methodology.sources?.brand_website_url && (
            <li className="flex items-start gap-2">
              <LinkIcon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Brand website: <a href={methodology.sources.brand_website_url} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{methodology.sources.brand_website_url}</a>{" "}
                <span className={`text-xs ${methodology.sources.brand_website_used ? "text-emerald-600" : "text-amber-600"}`}>({methodology.sources.brand_website_used ? "scraped ✓" : "unreachable"})</span>
              </span>
            </li>
          )}
          {methodology.sources?.merchant_url && (
            <li className="flex items-start gap-2">
              <LinkIcon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Merchant page: <a href={methodology.sources.merchant_url} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{methodology.sources.merchant_url}</a>{" "}
                <span className={`text-xs ${methodology.sources.merchant_page_used ? "text-emerald-600" : "text-amber-600"}`}>({methodology.sources.merchant_page_used ? "scraped ✓" : "unreachable"})</span>
              </span>
            </li>
          )}
          {methodology.sources?.certifications_declared?.length > 0 && (
            <li className="flex items-start gap-2">
              <FileText className="h-4 w-4 text-primary mt-0.5" />
              <span>Declared certifications: {methodology.sources.certifications_declared.join(", ")}</span>
            </li>
          )}
        </ul>
      </div>

      {/* Prompt disclosure */}
      <details className="card-premium p-6 group">
        <summary className="cursor-pointer text-sm font-semibold list-none flex items-center justify-between">
          View the exact prompt sent to Claude Sonnet 4.6
          <span className="text-xs text-muted-foreground group-open:hidden">Show</span>
          <span className="text-xs text-muted-foreground hidden group-open:inline">Hide</span>
        </summary>
        <div className="mt-4 space-y-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">System prompt</div>
            <pre className="text-[11px] mono bg-secondary rounded-2xl p-4 whitespace-pre-wrap max-h-64 overflow-auto">{methodology.system_prompt}</pre>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">User prompt</div>
            <pre className="text-[11px] mono bg-secondary rounded-2xl p-4 whitespace-pre-wrap max-h-64 overflow-auto">{methodology.user_prompt}</pre>
          </div>
        </div>
      </details>

      {/* Contest */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button className="btn-pill w-full bg-card border border-border hover:bg-muted" data-testid="contest-score-btn">
            <AlertTriangle className="h-4 w-4 mr-2" /> Contest this score
          </button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Contest this sustainability score</DialogTitle></DialogHeader>
          <form onSubmit={submitContest} className="mt-3 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Why do you disagree with this score?</label>
              <textarea rows={5} required minLength={15} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" data-testid="contest-reason" placeholder="Share evidence, certifications, or context we may have missed..." />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Email (optional, for follow-up)</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" data-testid="contest-email" />
            </div>
            <button type="submit" disabled={sending} className="btn-pill w-full bg-primary text-primary-foreground disabled:opacity-50" data-testid="contest-submit">
              {sending ? "Sending..." : "Submit for review"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MethodologyPanel;
